import React from "react"
import { createRoot } from "react-dom/client"

import PropTypes from "prop-types"
import {
  TouchableWithoutFeedback,
  Text,
  StyleSheet,
  Animated,
  Easing,
  View,
} from "react-native"
// import RootSiblings from 'react-native-root-siblings'
// import { isIphoneX } from 'react-native-iphone-x-helper'

import { AppColors, Units, AppFonts } from "@theme"

// Web-only singleton DOM portal mount point. RootSiblings (native) is not
// available on web, so Toast.show/hide render ToastContent into a single
// fixed <div> appended to document.body, created lazily on first use.
let toastContainer = null
let toastRoot = null

const getToastRoot = () => {
  if (typeof document === "undefined") return null
  if (!toastContainer) {
    toastContainer = document.createElement("div")
    toastContainer.setAttribute("id", "toast-portal-root")
    toastContainer.style.position = "fixed"
    toastContainer.style.left = "0"
    toastContainer.style.right = "0"
    toastContainer.style.bottom = "0"
    toastContainer.style.zIndex = "9999"
    toastContainer.style.pointerEvents = "none"
    document.body.appendChild(toastContainer)
    toastRoot = createRoot(toastContainer)
  }
  return toastRoot
}

// const defaultBottomOffset = isIphoneX() ? 48 : 8
const defaultBottomOffset = 8

// Plain function, NOT inside StyleSheet.create: react-native-web's StyleSheet.create does
// not pass function-valued entries through, so styles.container(...) threw at render time.
const containerStyle = (bottomOffset = 0) => ({
  position: "absolute",
  bottom: Units.responsiveValue(defaultBottomOffset) + bottomOffset,
  right: Units.responsiveValue(8),
  left: Units.responsiveValue(8),
  display: "flex",
})

const styles = StyleSheet.create({
  content: {
    alignItems: "center",
    backgroundColor: AppColors.brand.blackTwo,
    flex: 1,
    borderRadius: 8,
    display: "flex",
    elevation: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    minHeight: Units.responsiveValue(48),
    paddingHorizontal: Units.responsiveValue(12),
    paddingVertical: Units.responsiveValue(14),
    shadowColor: AppColors.brand.black,
    shadowOffset: { height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 4,
  },
  textContainer: {
    flex: 1,
    flexShrink: 2,
  },
  text: {
    ...AppFonts.mediumDemibold,
    color: AppColors.brand.white,
  },
  buttonText: {
    ...AppFonts.mediumDemibold,
    color: AppColors.brand.pink,
  },
})

export const ToastText = ({ numberOfLines, children, textAlign }) => (
  <Text style={[styles.text, { textAlign }]} numberOfLines={numberOfLines}>
    {children}
  </Text>
)

ToastText.propTypes = {
  children: PropTypes.string.isRequired,
  numberOfLines: PropTypes.number,
  textAlign: PropTypes.string,
}

export const ToastButton = ({ label, onPress }) => (
  <TouchableWithoutFeedback onPress={onPress}>
    <Text style={styles.buttonText}>{label}</Text>
  </TouchableWithoutFeedback>
)

ToastButton.propTypes = {
  label: PropTypes.string.isRequired,
  onPress: PropTypes.func.isRequired,
}

class ToastContent extends React.Component {
  state = {
    opacity: new Animated.Value(0),
    isAnimating: false,
  }

  hideTimer = null

  handlePress = () => {
    this.hide()
  }

  hide = byButtonPress => {
    const { isAnimating } = this.state
    if (!isAnimating) {
      return this.animateOpacity(
        {
          toValue: 0,
          easing: Easing.out(Easing.ease),
        },
        () => {
          this.props.onDismiss(byButtonPress)
          Toast.hide()
        }
      )
    }

    this.props.onDismiss(byButtonPress)
    return Toast.hide()
  }

  animateOpacity = (options, onFinished = () => {}) =>
    this.setState(
      () => ({ isAnimating: true }),
      () =>
        Animated.timing(this.state.opacity, {
          useNativeDriver: true,
          timing: 500,
          ...options,
        }).start(({ finished }) => {
          if (finished) {
            this.setState(() => ({ isAnimating: false }))
            onFinished()
          }
        })
    )

  handleButtonPress = () => {
    const { buttonOnPress } = this.props
    buttonOnPress()
    this.hide(true)
  }

  componentDidMount = () => {
    const { duration } = this.props
    this.animateOpacity(
      {
        toValue: 1,
        easing: Easing.out(Easing.ease),
      },
      () => {
        if (duration) {
          this.hideTimer = setTimeout(this.hide, duration)
        }
      }
    )
  }

  componentWillUnmount = () => {
    clearTimeout(this.hideTimer)
  }

  render = () => {
    const { children, bottomOffset, buttonLabel, numberOfLines, textAlign } =
      this.props
    const { opacity } = this.state
    return (
      <TouchableWithoutFeedback onPress={this.handlePress}>
        <Animated.View
          pointerEvents="auto"
          style={{
            opacity,
            ...containerStyle(bottomOffset),
          }}>
          <View style={styles.content}>
            <View style={styles.textContainer}>
              <ToastText numberOfLines={numberOfLines} textAlign={textAlign}>
                {children}
              </ToastText>
            </View>
            {buttonLabel ? (
              <ToastButton
                label={buttonLabel}
                onPress={this.handleButtonPress}
              />
            ) : null}
          </View>
        </Animated.View>
      </TouchableWithoutFeedback>
    )
  }
}

ToastContent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  bottomOffset: PropTypes.number,
  buttonLabel: PropTypes.string,
  buttonOnPress: PropTypes.func,
  onDismiss: PropTypes.func,
  duration: PropTypes.number,
  numberOfLines: PropTypes.number,
  textAlign: PropTypes.string,
}

ToastContent.defaultProps = {
  bottomOffset: 0,
  buttonLabel: "",
  buttonOnPress: () => {},
  onDismiss: () => {},
  duration: 3500,
  numberOfLines: null,
}

class Toast extends React.Component {
  toast = null

  // FIX 2026-07-04: RootSiblings is a React Native (iOS/Android) module and is not
  // available on web -- the previous implementation left the render commented out
  // and this stub a no-op, so every Toast.show() call site (canonical
  // isAlreadyEndedWarning/isAlreadyCancelledWarning language, gameplansCardContainer,
  // etc.) silently showed nothing. On web, mount a single ToastContent instance into a
  // singleton DOM portal appended to document.body via ReactDOM createRoot. Signature
  // is unchanged so every existing call site lights up with no edits required there.
  static show = (content, options = { bottomOffset: 58 }) => {
    const root = getToastRoot()
    if (!root) return null

    const handleDismiss = () => {
      root.render(null)
    }

    root.render(
      <ToastContent {...options} onDismiss={handleDismiss}>
        {content}
      </ToastContent>
    )

    return root
  }

  static hide = () => {
    if (toastRoot) {
      toastRoot.render(null)
    }
  }

  componentDidMount() {
    // this.toast = new RootSiblings(<ToastContent {...this.props} />)
  }

  render = () => null
}

export default Toast
